import type { Submission } from '../types/uploads';

export interface LocalUploadProgressObservation {
  lastOffset: number;
  lastAdvancedAt: number;
  interrupted: boolean;
}

export type LocalUploadProgressObservations = ReadonlyMap<string, LocalUploadProgressObservation>;

/** Preserve Set identity when a derived session-id collection has not changed. React effects that
 * observe progress use this guard so equivalent snapshots cannot create a render feedback loop. */
export function sameSessionIds(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  if (left.size !== right.size) return false;
  for (const sessionId of left) if (!right.has(sessionId)) return false;
  return true;
}

/** Observation reducers retain unchanged value objects, so reference comparison detects whether an
 * active progress snapshot actually advanced, appeared, or disappeared. */
export function sameProgressObservations(
  left: LocalUploadProgressObservations,
  right: LocalUploadProgressObservations,
): boolean {
  if (left.size !== right.size) return false;
  for (const [sessionId, observation] of left) {
    if (right.get(sessionId) !== observation) return false;
  }
  return true;
}

export interface LocalUploadProgressSample {
  sessionId: string;
  offset: number;
}

/** Merge browser-observed byte progress into the client observation baseline. */
export function observeProgressOffsets(
  previous: LocalUploadProgressObservations,
  samples: readonly LocalUploadProgressSample[],
  observedAt: number,
): Map<string, LocalUploadProgressObservation> {
  const next = new Map<string, LocalUploadProgressObservation>();
  for (const sample of samples) {
    const existing = previous.get(sample.sessionId);
    if (!existing || sample.offset > existing.lastOffset) {
      next.set(sample.sessionId, {
        lastOffset: sample.offset,
        lastAdvancedAt: observedAt,
        interrupted: false,
      });
      continue;
    }
    next.set(sample.sessionId, existing);
  }
  return next;
}

/** Merge the latest durable `report-progress` values into the client observation baseline. */
export function observeReportedProgress(
  previous: LocalUploadProgressObservations,
  submissions: readonly Submission[],
  observedAt: number,
): Map<string, LocalUploadProgressObservation> {
  return observeProgressOffsets(
    previous,
    submissions.map((submission) => ({
      sessionId: submission.sessionId,
      offset: submission.receivedBytes,
    })),
    observedAt,
  );
}

/** Apply an authoritative GCS committed-offset probe. A failed/missing or regressed probe is ignored;
 * only an equal offset sustained for the whole threshold becomes interrupted. */
export function observeGcsOffsets(
  previous: LocalUploadProgressObservations,
  offsets: ReadonlyMap<string, number>,
  observedAt: number,
  stallThresholdMs: number,
): Map<string, LocalUploadProgressObservation> {
  const next = new Map(previous);
  for (const [sessionId, existing] of previous) {
    const offset = offsets.get(sessionId);
    if (offset === undefined || offset < existing.lastOffset) continue;
    if (offset > existing.lastOffset) {
      next.set(sessionId, {
        lastOffset: offset,
        lastAdvancedAt: observedAt,
        interrupted: false,
      });
      continue;
    }
    next.set(sessionId, {
      ...existing,
      interrupted: observedAt - existing.lastAdvancedAt >= stallThresholdMs,
    });
  }
  return next;
}

/** Confirm a live-panel stall against GCS. Browser progress can be ahead of the committed GCS
 * offset while a chunk is in flight, so an equal or lower GCS offset after the full quiet window is
 * positive interruption evidence. Only a GCS offset beyond the browser's last observed position
 * resets the activity window. Missing probes remain inconclusive. */
export function confirmLiveUploadStalls(
  previous: LocalUploadProgressObservations,
  offsets: ReadonlyMap<string, number>,
  observedAt: number,
  stallThresholdMs: number,
): Map<string, LocalUploadProgressObservation> {
  const next = new Map(previous);
  for (const [sessionId, existing] of previous) {
    if (observedAt - existing.lastAdvancedAt < stallThresholdMs) continue;
    const offset = offsets.get(sessionId);
    if (offset === undefined) continue;
    if (offset > existing.lastOffset) {
      next.set(sessionId, {
        lastOffset: offset,
        lastAdvancedAt: observedAt,
        interrupted: false,
      });
      continue;
    }
    next.set(sessionId, { ...existing, interrupted: true });
  }
  return next;
}

export function interruptedSessionIds(
  observations: LocalUploadProgressObservations,
): ReadonlySet<string> {
  return new Set(
    [...observations]
      .filter(([, observation]) => observation.interrupted)
      .map(([sessionId]) => sessionId),
  );
}
